import { call, select, takeLatest } from 'redux-saga/effects'
import getAPIConfig from '@/shared/tools/getAPIConfig'
import { selectAuthData } from '@/containers/Login/AuthSelectors'
import {
  customerModuleActions,
  registerToNewsletter,
  verifyEmail, verifyItalyFiscalCode,
  verifySiret
} from './ModulesPanel/Modules/CustomerModule/CustomerModuleSagas'
import {
  unlockMcpClient,
  updateZohoId,
  verifyZohoId,
  zohoModuleActions,
} from './ModulesPanel/Modules/ZohoModule/ZohoModuleSagas'
import {
  characteristicsModuleActions,
  cityEligibilityCheck,
  loadTelecomSuppliers,
  searchAddress,
} from './ModulesPanel/Modules/CharacteristicsModule/CharacteristicsModuleSagas'
import {
  loadPropaneEnergySuppliers,
  loadPropaneQuoteReasons,
  loadPropaneVisibility,
  propaneModuleActions,
} from './ModulesPanel/Modules/PropaneModule/PropaneModuleSagas'
import {
  calculateOfferPrice,
  consumptionModuleActions,
} from './ModulesPanel/Modules/ConsumptionModule/ConsumptionModuleSagas'
import {
  loadEnergyOrigin,
  loadOfferInformation,
  loadOffers,
  loadProviderInformation,
  loadProviderOptions,
  loadProviders,
  offerModuleActions,
} from './ModulesPanel/Modules/OfferModule/OfferModuleSagas'
import {
  billingModuleActions,
  fetchPromoCode,
} from './ModulesPanel/Modules/BillingPaymentModule/BillingPaymentModuleSagas'
import {
  checkDuplicatedMeter,
  loadGasEnergySuppliers,
  pceModuleActions,
  requestMeterData,
  validateMeterSyntax,
} from './ModulesPanel/Modules/PceModule/PceModuleSagas'
import {
  checkDuplicatedPdlMeter,
  loadElectricityEnergySuppliers,
  pdlModuleActions,
  requestPdlMeterData,
  retrievePdlCacheDataForKey,
  validatePdlMeterSyntax,
} from './ModulesPanel/Modules/PdlModule/PdlModuleSagas'
import {
  carEstimatorRetrieveEstimation,
  carEstimatorToolActions,
} from './ToolsPanel/Modules/CarEstimationTool/CarEstimatorToolSagas'
import {
  additionalSalesActions,
  openPandaDocSignLink,
  resendNeatEmail,
  sendAdditionalSalesCodeValidation,
} from './AdditionalSalesPanel/AdditionalSalesSagas'

export const subscriptionFormActions = {
  ...zohoModuleActions,
  ...customerModuleActions,
  ...characteristicsModuleActions,
  ...propaneModuleActions,
  ...offerModuleActions,
  ...billingModuleActions,
  ...pceModuleActions,
  ...pdlModuleActions,
  ...consumptionModuleActions,
  ...carEstimatorToolActions,
  ...additionalSalesActions,
  GENERIC_RETRIEVE_CACHE_DATA: 'GENERIC_RETRIEVE_CACHE_DATA',
}

export function* retrieveCacheDataForKey({ key, route, subscriptionId, callback }) {
  const { authTokenSession, lang } = yield select(selectAuthData)
  try {
    const config = yield call(getAPIConfig, { method: 'get', authTokenSession })
    const response = yield call(fetch, route + '/' + key + '/' + subscriptionId + '?lang=' + lang, config)
    const responseData = yield call([response, response.json])
    yield call(callback, responseData, response.status)
  } catch (error) {
    console.warn('retrieveCacheDataError', error)
  }
}

export default function* subscriptionFormSagas() {
  yield takeLatest(subscriptionFormActions.ZOHO_MODULE_VERIFY_ID, verifyZohoId)
  yield takeLatest(subscriptionFormActions.ZOHO_MODULE_UPDATE_ID, updateZohoId)
  yield takeLatest(subscriptionFormActions.CUSTOMER_MODULE_VERIFY_EMAIL, verifyEmail)
  yield takeLatest(subscriptionFormActions.CUSTOMER_MODULE_VERIFY_SIRET, verifySiret)
  yield takeLatest(subscriptionFormActions.CUSTOMER_MODULE_REGISTER_TO_NEWSLETTER, registerToNewsletter)
  yield takeLatest(subscriptionFormActions.GENERIC_RETRIEVE_CACHE_DATA, retrieveCacheDataForKey)
  yield takeLatest(subscriptionFormActions.RETRIEVE_PDL_CACHE_DATA, retrievePdlCacheDataForKey)
  yield takeLatest(subscriptionFormActions.ZOHO_MODULE_UNLOCK_MCP_CLIENT, unlockMcpClient)
  yield takeLatest(subscriptionFormActions.SEARCH_ADDRESS, searchAddress)
  yield takeLatest(subscriptionFormActions.CITY_ELIGIBILITY_CHECK, cityEligibilityCheck)
  yield takeLatest(subscriptionFormActions.PROPANE_LOAD_ENERGY_SUPPLIERS, loadPropaneEnergySuppliers)
  yield takeLatest(subscriptionFormActions.OFFER_MODULE_LOAD_PROVIDERS, loadProviders)
  yield takeLatest(subscriptionFormActions.OFFER_MODULE_LOAD_OFFERS, loadOffers)
  yield takeLatest(subscriptionFormActions.OFFER_MODULE_LOAD_PROVIDERS_OPTIONS, loadProviderOptions)
  yield takeLatest(subscriptionFormActions.OFFER_MODULE_LOAD_OFFER_INFORMATION, loadOfferInformation)
  yield takeLatest(subscriptionFormActions.OFFER_MODULE_LOAD_PROVIDER_INFORMATION, loadProviderInformation)
  yield takeLatest(subscriptionFormActions.OFFER_MODULE_LOAD_ENERGY_ORIGIN, loadEnergyOrigin)
  yield takeLatest(subscriptionFormActions.BILLING_MODULE_FETCH_PROMO_CODE, fetchPromoCode)
  yield takeLatest(subscriptionFormActions.METER_MODULE_CHECK_DUPLICATED, checkDuplicatedMeter)
  yield takeLatest(subscriptionFormActions.METER_MODULE_CHECK_DUPLICATED_PDL, checkDuplicatedPdlMeter)
  yield takeLatest(subscriptionFormActions.METER_MODULE_REQUEST_DATA, requestMeterData)
  yield takeLatest(subscriptionFormActions.METER_MODULE_REQUEST_PDL_DATA, requestPdlMeterData)
  yield takeLatest(subscriptionFormActions.METER_MODULE_VALIDATE_METER_SYNTAX, validateMeterSyntax)
  yield takeLatest(subscriptionFormActions.METER_MODULE_VALIDATE_PDL_METER_SYNTAX, validatePdlMeterSyntax)
  yield takeLatest(subscriptionFormActions.METER_MODULE_LOAD_ENERGY_SUPPLIERS_ELEC, loadElectricityEnergySuppliers)
  yield takeLatest(subscriptionFormActions.METER_MODULE_LOAD_ENERGY_SUPPLIERS_GAS, loadGasEnergySuppliers)
  yield takeLatest(subscriptionFormActions.CONSUMPTION_MODULE_CALCULATE_PRICE, calculateOfferPrice)
  yield takeLatest(subscriptionFormActions.CAR_ESTIMATOR_TOOL_ESTIMATE, carEstimatorRetrieveEstimation)
  yield takeLatest(subscriptionFormActions.PROPANE_LOAD_QUOTE_REASONS, loadPropaneQuoteReasons)
  yield takeLatest(subscriptionFormActions.PROPANE_LOAD_VISIBILITY, loadPropaneVisibility)
  yield takeLatest(subscriptionFormActions.ADDITIONAL_SALES_SEND_VALIDATION_CODE, sendAdditionalSalesCodeValidation)
  yield takeLatest(subscriptionFormActions.ADDITIONAL_SALES_OPEN_SIGN_LINK, openPandaDocSignLink)
  yield takeLatest(subscriptionFormActions.ADDITIONAL_SALES_NEAT_RESEND_EMAIL, resendNeatEmail)
  yield takeLatest(subscriptionFormActions.LOAD_TELECOM_SUPPLIERS, loadTelecomSuppliers)
  yield takeLatest(subscriptionFormActions.CUSTOMER_MODULE_VERIFY_ITALY_FISCAL_CODE, verifyItalyFiscalCode)
}
